import { CloseRounded, ExpandMoreRounded } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Skeleton,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import AcxDrawer from "components/UI/Drawer/AcxDrawer";
import { ServiceError } from "services/BaseService";
import {
    KboFlowAnalysisMaterial,
    KboFlowAnalysisMaterialItem,
    KboFlowDebugService,
} from "services/KboFlowDebugService";
import { formatNumber } from "../KboFlowDebug/KboFlowDebugUI";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";

const previewCache = new Map<string, KboFlowAnalysisMaterial>();
const fullCache = new Map<string, KboFlowAnalysisMaterial>();

function isUnavailableMaterialError(error: unknown) {
    return (
        error instanceof ServiceError &&
        [404, 409].includes(error.response.status)
    );
}

function materialSourceLabel(source?: string) {
    return source?.replace(/[^a-z]/gi, "").toLowerCase() === "modelreasoning"
        ? "reasoning"
        : "summaries";
}

function MaterialRow({ item }: { item: KboFlowAnalysisMaterialItem }) {
    return (
        <Box
            sx={{
                bgcolor: item.matchesResult ? pageColors.greenSoft : undefined,
                borderBottom: "1px solid",
                borderColor: item.matchesResult ? "#BBE5CE" : "divider",
                px: { xs: 1.25, md: 1.75 },
                py: 1.25,
            }}
        >
            <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: pageColors.ink,
                            fontSize: 14,
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {item.text ||
                            "No material is currently available for this conversation."}
                    </Typography>
                    <Typography
                        sx={{ color: pageColors.muted, fontSize: 12, mt: 0.35 }}
                    >
                        #{item.displayOrder}
                        {` · ${item.conversationId}`}
                    </Typography>
                </Box>
                <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                    {item.matchesResult && (
                        <Chip
                            aria-label="Matches this result"
                            label="Matches result"
                            size="small"
                            sx={{
                                bgcolor: pageColors.greenSoft,
                                color: pageColors.green,
                                fontWeight: 700,
                            }}
                        />
                    )}
                    {item.wasAnalyzed && (
                        <Chip
                            label="Analyzed"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export function AnalysisMaterialAccordion({
    runId,
    nodeId,
    resultId,
    resultName,
    hasMatchingConversationIds,
    titleColor,
    badge,
    onViewFullPath,
}: {
    runId?: string;
    nodeId?: string;
    resultId?: string;
    resultName?: string;
    hasMatchingConversationIds: boolean;
    /** Overrides the header title color; defaults to the accent green used for the primary result. */
    titleColor?: string;
    /** An optional, non-interactive indicator (e.g. an outcome chip) shown at the end of the header. */
    badge?: React.ReactNode;
    /**
     * When provided, shows a "View full path" button that navigates to this result's own section of the
     * page — the behavior a click used to have before this result became an accordion.
     */
    onViewFullPath?: () => void;
}) {
    const service = useMemo(() => new KboFlowDebugService(), []);
    const cacheKey =
        runId && nodeId && resultId
            ? `${runId}:${nodeId}:${resultId}`
            : undefined;
    const [expanded, setExpanded] = useState(false);
    const [preview, setPreview] = useState<KboFlowAnalysisMaterial | undefined>(
        () => (cacheKey ? previewCache.get(cacheKey) : undefined),
    );
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string>();
    const [previewUnavailable, setPreviewUnavailable] = useState(false);
    const [previewAttempt, setPreviewAttempt] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [full, setFull] = useState<KboFlowAnalysisMaterial | undefined>(() =>
        cacheKey ? fullCache.get(cacheKey) : undefined,
    );
    const [fullLoading, setFullLoading] = useState(false);
    const [fullError, setFullError] = useState<string>();
    const [fullUnavailable, setFullUnavailable] = useState(false);
    const fullRequest = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!expanded || preview || !runId || !nodeId || !resultId)
            return undefined;
        const controller = new AbortController();
        setPreviewLoading(true);
        setPreviewError(undefined);
        setPreviewUnavailable(false);
        service
            .getAnalysisMaterialPreview(
                runId,
                nodeId,
                resultId,
                controller.signal,
            )
            .then((response) => {
                if (cacheKey) previewCache.set(cacheKey, response);
                setPreview(response);
            })
            .catch((error: unknown) => {
                if (!controller.signal.aborted) {
                    const unavailable = isUnavailableMaterialError(error);
                    setPreviewUnavailable(unavailable);
                    setPreviewError(
                        unavailable
                            ? "This run does not retain supporting material for this result."
                            : error instanceof Error
                            ? error.message
                            : "Unable to load analysis material.",
                    );
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setPreviewLoading(false);
            });
        return () => controller.abort();
    }, [
        cacheKey,
        expanded,
        nodeId,
        preview,
        previewAttempt,
        resultId,
        runId,
        service,
    ]);

    useEffect(() => () => fullRequest.current?.abort(), []);

    const openDrawer = () => {
        if (!runId || !nodeId || !resultId) return;
        setDrawerOpen(true);
        if (full) return;
        fullRequest.current?.abort();
        const controller = new AbortController();
        fullRequest.current = controller;
        setFullLoading(true);
        setFullError(undefined);
        setFullUnavailable(false);
        service
            .getAnalysisMaterial(runId, nodeId, resultId, controller.signal)
            .then((response) => {
                if (cacheKey) fullCache.set(cacheKey, response);
                setFull(response);
            })
            .catch((error: unknown) => {
                if (!controller.signal.aborted) {
                    const unavailable = isUnavailableMaterialError(error);
                    setFullUnavailable(unavailable);
                    setFullError(
                        unavailable
                            ? "This run does not retain supporting material for this result."
                            : error instanceof Error
                            ? error.message
                            : "Unable to load all analysis material.",
                    );
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setFullLoading(false);
            });
    };

    const closeDrawer = () => {
        fullRequest.current?.abort();
        setDrawerOpen(false);
        setFullLoading(false);
    };

    if (!runId || !nodeId || !resultId) return null;

    return (
        <>
            <Accordion
                disableGutters
                expanded={expanded}
                onChange={(_event, open) => setExpanded(open)}
                sx={{
                    "&:before": { display: "none" },
                    border: "1px solid",
                    borderColor: pageColors.greenSoft,
                    borderRadius: "8px !important",
                    mt: 2,
                    overflow: "hidden",
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            gap: 1,
                            minWidth: 0,
                            width: "100%",
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    color: titleColor ?? pageColors.green,
                                    fontWeight: 800,
                                }}
                            >
                                {resultName}
                            </Typography>
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    fontSize: 13,
                                    mt: 0.2,
                                }}
                            >
                                {preview
                                    ? `${formatNumber(
                                          preview.analyzedConversationCount,
                                      )} analyzed ${materialSourceLabel(
                                          preview.materialSource,
                                      )}${
                                          preview.reasoningModelName
                                              ? ` · ${preview.reasoningModelName}`
                                              : ""
                                      }`
                                    : "Read the material KBo used for this analysis"}
                            </Typography>
                        </Box>
                        {badge}
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        borderTop: "1px solid",
                        borderColor: pageColors.greenSoft,
                        p: 0,
                    }}
                >
                    {previewLoading && (
                        <Box>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                        px: { xs: 1.25, md: 1.75 },
                                        py: 1.25,
                                    }}
                                >
                                    <Skeleton variant="text" width="90%" />
                                    <Skeleton variant="text" width="30%" />
                                </Box>
                            ))}
                        </Box>
                    )}
                    {previewError && (
                        <Box sx={{ p: 1.5 }}>
                            <Alert
                                action={
                                    previewUnavailable ? undefined : (
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() =>
                                                setPreviewAttempt(
                                                    (value) => value + 1,
                                                )
                                            }
                                        >
                                            Retry
                                        </Button>
                                    )
                                }
                                severity={previewUnavailable ? "info" : "error"}
                            >
                                {previewError}
                            </Alert>
                        </Box>
                    )}
                    {preview && (
                        <>
                            {!hasMatchingConversationIds && (
                                <Alert severity="info" sx={{ borderRadius: 0 }}>
                                    This older run does not retain the exact
                                    conversations that matched this result.
                                </Alert>
                            )}
                            {preview.items.length ? (
                                <Box>
                                    {preview.items.map((item) => (
                                        <MaterialRow
                                            item={item}
                                            key={item.conversationId}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography
                                    color="text.secondary"
                                    sx={{ p: 2 }}
                                >
                                    No current{" "}
                                    {materialSourceLabel(
                                        preview.materialSource,
                                    )}{" "}
                                    are available for this dimension value.
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", gap: 1, p: 1.25 }}>
                                <Button
                                    onClick={openDrawer}
                                    size="small"
                                    variant="outlined"
                                >
                                    View all{" "}
                                    {formatNumber(
                                        preview.totalConversationCount,
                                    )}{" "}
                                    conversations
                                </Button>
                                {onViewFullPath && (
                                    <Button
                                        onClick={onViewFullPath}
                                        size="small"
                                        variant="text"
                                    >
                                        View full path
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
            <AcxDrawer
                anchor="right"
                collapsible="full"
                customPaperSx={{
                    maxWidth: "100vw",
                    width: { xs: "100vw", sm: 680 },
                }}
                customRootStyles={{ zIndex: 1400, whiteSpace: "normal" }}
                disableEnforceFocus
                onDrawerClose={closeDrawer}
                open={drawerOpen}
                content={
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                alignItems: "flex-start",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                gap: 1,
                                p: 2,
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6">
                                    {resultName ?? "Analysis result"}
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    variant="body2"
                                >
                                    All current{" "}
                                    {materialSourceLabel(
                                        full?.materialSource ??
                                            preview?.materialSource,
                                    )}{" "}
                                    for this dimension value
                                </Typography>
                                {full && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.75,
                                            mt: 0.75,
                                        }}
                                    >
                                        <Chip
                                            label={`${formatNumber(
                                                full.totalConversationCount,
                                            )} in population`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`${formatNumber(
                                                full.analyzedConversationCount,
                                            )} analyzed`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        {hasMatchingConversationIds && (
                                            <Chip
                                                label={`${formatNumber(
                                                    full.matchingConversationCount,
                                                )} match this result`}
                                                size="small"
                                                sx={{
                                                    bgcolor:
                                                        pageColors.greenSoft,
                                                    color: pageColors.green,
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <IconButton
                                aria-label="Close analysis material"
                                onClick={closeDrawer}
                                size="small"
                            >
                                <CloseRounded />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                            {fullLoading && (
                                <Box
                                    sx={{
                                        alignItems: "center",
                                        display: "flex",
                                        gap: 1,
                                        p: 2,
                                    }}
                                >
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">
                                        Loading all material…
                                    </Typography>
                                </Box>
                            )}
                            {fullError && (
                                <Box sx={{ p: 2 }}>
                                    <Alert
                                        action={
                                            fullUnavailable ? undefined : (
                                                <Button
                                                    color="inherit"
                                                    onClick={openDrawer}
                                                    size="small"
                                                >
                                                    Retry
                                                </Button>
                                            )
                                        }
                                        severity={
                                            fullUnavailable ? "info" : "error"
                                        }
                                    >
                                        {fullError}
                                    </Alert>
                                </Box>
                            )}
                            {full &&
                                (full.items.length ? (
                                    full.items.map((item) => (
                                        <MaterialRow
                                            item={item}
                                            key={item.conversationId}
                                        />
                                    ))
                                ) : (
                                    <Typography
                                        color="text.secondary"
                                        sx={{ p: 2 }}
                                    >
                                        No current material is available for
                                        this dimension value.
                                    </Typography>
                                ))}
                        </Box>
                    </Box>
                }
            />
        </>
    );
}
